import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import NewsCard from "@/components/feature/NewsCard";
import { getCategoryByKey } from "@/types/content";
import { filterAndSort, getByCategory } from "@/lib/content";
import FilterBar, { type FilterState } from "./components/FilterBar";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const category = getCategoryByKey(type);

  const [filters, setFilters] = useState<FilterState>({
    subType: "全部",
    timeRange: "全部",
    hotOnly: false,
    sort: "latest",
  });
  const [visible, setVisible] = useState(PAGE_SIZE);

  const list = useMemo(() => {
    if (!category) return [];
    return filterAndSort(getByCategory(category.key), filters);
  }, [category, filters]);

  usePageMeta({
    title: category
      ? `小蜜桃 - ${category.name}资讯与推荐`
      : "小蜜桃 - 分类不存在",
    description: category
      ? `查看小蜜桃提供的最新${category.name}相关娱乐资讯和精选内容。`
      : "该分类不存在，请返回首页浏览小蜜桃的其他娱乐内容。",
    keywords: category
      ? `小蜜桃,${category.name},娱乐资讯,推荐内容`
      : "小蜜桃,娱乐资讯",
  });

  if (!category) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 flex flex-col items-center text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">
            没有找到这个分类
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的分类可能已被移除，去首页看看其他精彩内容吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const updateFilters = (next: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setVisible(PAGE_SIZE);
  };

  const resetFilters = () => {
    setFilters({ subType: "全部", timeRange: "全部", hotOnly: false, sort: "latest" });
    setVisible(PAGE_SIZE);
  };

  const shown = list.slice(0, visible);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>分类</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{category.name}</span>
        </nav>

        <div className="mt-5 flex items-center gap-4">
          <span className="w-12 h-12 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              {category.name}频道
            </h1>
            <p className="mt-1 text-sm text-foreground-500">{category.description}</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-6 md:py-8 flex flex-col gap-6">
        <FilterBar
          category={category}
          filters={filters}
          onChange={updateFilters}
          onReset={resetFilters}
        />

        <div className="flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-primary-600 font-medium">{list.length}</span> 条内容
          </p>
          {(filters.subType !== "全部" ||
            filters.timeRange !== "全部" ||
            filters.hotOnly) && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              清除全部筛选
            </button>
          )}
        </div>

        {shown.length === 0 ? (
          <div className="border border-dashed border-background-300 rounded-lg py-16 flex flex-col items-center text-center">
            <span className="w-12 h-12 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-foreground-700 font-medium">没有符合条件的内容</p>
            <p className="mt-1 text-sm text-foreground-500">
              试着放宽筛选条件，或重置后再看看
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
              {shown.map((item, idx) => (
                <NewsCard key={item.id} item={item} variant="list" index={idx} />
              ))}
            </div>

            {visible < list.length && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 border border-background-200 text-foreground-700 text-sm px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  加载更多（剩余 {list.length - visible} 条）
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}